import {
  call,
  cancelled,
  delay,
  getContext,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';
import { damzOffline } from '../../actions/initialization';
import {
  closeRoom,
  markRoomRead,
  maybeDisplayTypingIndicator,
  presenceJoin,
  presenceLeave,
  selectRoom,
  setUserTyping,
  stopTyping,
  updateDrawerRoomLastMsg,
} from '../../actions/room';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import ddt from '@wix/da-ddt';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import {
  createLastMsgFromSentMessage,
  getMessageCursor,
  getRoomStreamId,
  messagesFetchToMessageItem,
} from '../../helpers';
import { buffers, eventChannel } from 'redux-saga';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { PapiUser } from '@wix/da-papi-types';
import { appVisibilityChange } from '@wix/da-shared-react/pkg/redux/app/actions';
import { withCursor } from '@wix/da-shared-react/pkg/Stream';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import { getRoomUnreadState } from '../../selectors/roomUnread';
import {
  shouldAppendNewMessages,
  shouldClearUnreadCount,
  shouldUpdateLastViewedMessage,
  updateRoomStateForNewMessages,
} from '../../helpers/roomState';
import {
  markLastViewedMessage,
  updateRoomUnreadState,
} from '../../actions/roomUnread';
import { moveRoomToDrawerHead } from '../helpers';
import { realtimeConnections } from '../../RealtimeConnections';
import { MessageItem } from '../../../types';
import {
  findItemInStreams,
  getStream,
} from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { DamzPubSub } from '../../../hooks/useDamzPubSub';
import { roomStreamGroupId } from '../../../constants';
import { invalidateReactionUsersList } from '../../actions/message';

export function* updateRoomDrawerPreviewIfLastMessageEdited(
  room_id: string,
  newMessage: MessageItem
) {
  const { items }: ReturnType<typeof getStream<MessageItem>> = yield select(
    getStream,
    getRoomStreamId(room_id)
  );

  if (items.length === 0) {
    return;
  }

  const lastMessage = items[items.length - 1];

  if (
    items.length > 0 &&
    lastMessage.msg.message_id === newMessage.msg.message_id &&
    newMessage.user
  ) {
    yield put(
      updateDrawerRoomLastMsg(room_id, {
        msg: newMessage.msg.msg,
        user: newMessage.user,
        message_id: newMessage.msg.message_id,
        deleted_at: newMessage.msg.deleted_at,
      })
    );
    return;
  }
}

/**
 * This merges the incoming reactions with the old to keep the old order so
 * that adding new reactions doesn't changes the order (stops UI jumping)
 */
function mergeKeepOld(oldObj, newObj) {
  const result = {};

  for (const key of Object.keys(newObj)) {
    // Keep old value if exists, otherwise add new
    result[key] = oldObj[key] ?? newObj[key];
    result[key].count = newObj[key].count;
  }

  return result;
}

export function* listenToRoomChannel(room_id) {
  // Validate room_id parameter
  if (!room_id) {
    ddt.log('damz', 'Cannot listen to room channel - room_id is undefined');
    return;
  }

  // We need to return here when faking API requests to avoid this saga holding everything up.
  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  if (doFake) {
    return;
  }

  const client: SupabaseClient = yield getContext('supaClient');
  const currentUser: PapiUser = yield select(getCurrentUser);

  try {
    yield client.realtime.setAuth(); // fixes - https://github.com/supabase/realtime/issues/1111
    realtimeConnections.setClient(client);
    const bindEmit = realtimeConnections.startRoomChannel(
      room_id,
      currentUser.useridUuid
    );

    const sagaChannel = eventChannel(
      emitter => bindEmit(emitter),
      buffers.expanding<any>()
    );

    while (true) {
      try {
        const payload: {
          chan: any;
          switchRoom: ReturnType<typeof selectRoom>;
          offline: any;
          appVisibility: ReturnType<typeof appVisibilityChange>;
          setUserTyping: ReturnType<typeof setUserTyping>;
          closed: ReturnType<typeof closeRoom>;
        } = yield race({
          chan: take(sagaChannel),
          switchRoom: take(selectRoom),
          offline: take(damzOffline),
          appVisibility: take(appVisibilityChange),
          setUserTyping: take(setUserTyping),
          closed: take(closeRoom),
        });
        // Close the channel when we offline or switch room or when the room is closed
        if (
          payload.offline ||
          (payload.switchRoom &&
            payload.switchRoom.payload.room_id !== room_id) ||
          payload.closed
        ) {
          sagaChannel.close();
          break;
        }

        // Handle typing events
        if (payload.setUserTyping) {
          // Must keep this in sync with the other track data above (currently we just need to re-include user_id)
          yield realtimeConnections.roomTrack({
            user_id: currentUser.useridUuid,
            username: currentUser.username,
            typing: Date.now(),
          });
        }

        // Handle new messages coming in via realtime
        if (payload.chan) {
          // Handle messages
          if (payload.chan.type === 'message') {
            ddt.log('damz', 'Got new message', payload);
            yield put(stopTyping()); // Stop typing indicator whenever new message is received
            const msgs = messagesFetchToMessageItem([
              payload.chan.data.payload,
            ]);
            const roomState = yield select(getRoomUnreadState, room_id);

            if (shouldAppendNewMessages(roomState)) {
              yield put(
                withCursor.actions.insertItemsAtOffsetAndSetCursor({
                  streamId: getRoomStreamId(room_id),
                  items: msgs,
                  newCursor: getMessageCursor(
                    msgs[0].msg.message_id,
                    msgs[0].msg.sent_at
                  ),
                  offset: -1,
                })
              );
            }
            // Update the last message in the drawer stream
            const lastMsg = createLastMsgFromSentMessage(
              msgs[0].msg.msg,
              msgs[0].user ?? currentUser,
              msgs[0].msg.message_id
            );
            yield put(updateDrawerRoomLastMsg(room_id, lastMsg));

            // Update the room unread states
            const updates = updateRoomStateForNewMessages(roomState, 1);
            ddt.log(
              'damz.unread',
              'Updating room state for new message',
              room_id,
              updates
            );
            yield put(updateRoomUnreadState(room_id, updates));

            if (shouldClearUnreadCount(roomState)) {
              ddt.log(
                'damz.unread',
                'Clearing unread count on new message',
                room_id
              );
              yield put(markRoomRead(room_id));
            }

            if (shouldUpdateLastViewedMessage(roomState)) {
              yield put(markLastViewedMessage(room_id, msgs[0].msg.message_id));
              ddt.log('damz.unread', 'Updating last viewed message', room_id);
            }

            yield call(moveRoomToDrawerHead, room_id);
          }

          if (payload.chan.type === 'update') {
            ddt.log('damz', 'Got update message', payload);
            const msgs = messagesFetchToMessageItem([
              payload.chan.data.payload,
            ]);
            const currentMessage = yield select(state =>
              findItemInStreams(
                state,
                roomStreamGroupId,
                (item: MessageItem) => {
                  return item.msg.message_id === msgs[0].msg.message_id;
                }
              )
            );
            // Stablize reactions
            if (currentMessage) {
              msgs[0].msg.reactions = mergeKeepOld(
                currentMessage.msg.reactions,
                msgs[0].msg.reactions
              );

              if (msgs[0].msg.reactions !== currentMessage.msg.reactions) {
                yield put(invalidateReactionUsersList(msgs[0].msg.message_id));
              }
            }
            yield put(
              withCursor.actions.replaceItem({
                streamId: getRoomStreamId(room_id),
                newItem: msgs[0],
                predicate: (item: any) => {
                  return item.msg.message_id === msgs[0].msg.message_id;
                },
              })
            );
            const pubsub: DamzPubSub = globalThis.pubsub;
            if (pubsub) {
              pubsub.publish('msg', 'updated');
            }
            yield call(
              updateRoomDrawerPreviewIfLastMessageEdited,
              room_id,
              msgs[0]
            );
          }

          // Store who is present in the room, we use this to optimise the fan-out
          // on the server and this list is sent with the send.
          if (payload.chan.type === 'presence_join') {
            yield put(presenceJoin(room_id, payload.chan.data.newPresences));
          }

          if (payload.chan.type === 'presence_leave') {
            yield put(presenceLeave(room_id, payload.chan.data.leftPresences));
          }

          if (payload.chan.type === 'presence_sync') {
            yield put(maybeDisplayTypingIndicator(payload.chan));
          }

          // Track presence when window is hidden/shown
          if (payload.appVisibility) {
            if (payload.appVisibility.payload.hidden) {
              yield realtimeConnections.roomUnTrack();
            } else {
              yield realtimeConnections.roomTrack({
                user_id: currentUser.useridUuid,
              });
            }
          }
        }
      } catch (err) {
        ddt.error('damz', 'Realtime room processing error', err);
        sagaChannel.close();
      }
    }
  } catch (err) {
    ddt.error('damz', 'Error creating saga channel', err);
    yield put(addErrorNotification(i18next.t('damz:damz.realtime.room.error')));
  } finally {
    if (yield cancelled()) {
      realtimeConnections.disconnectRoomChannel();
    }
  }
}

export function* attemptRejoinIfChannelsErrored() {
  ddt.log('damz.auth', 'Attempting to rejoin if channels errored');

  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  yield refreshSessionIfNeeded();

  const channels = client.getChannels();
  for (const ch of channels) {
    if (ch.state === 'errored') {
      ddt.log('damz.auth', 'Channel errored, scheduling rejoin', ch);
      ch.rejoinTimer.scheduleTimeout();
      yield delay(100);
    }
  }

  return channels;
}

function* refreshSessionIfNeeded() {
  ddt.log('damz.auth', 'Refreshing session if needed');

  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  const { data, error } = yield client.auth.getSession();
  if (error || !data.session) {
    if (document.hidden) {
      return;
    }
    ddt.alert('damz', 'Failed to get session', ['damz', 'realtime'], {
      error: error ? error.message : 'unknown',
      errorCode: error ? error.code : 'unknown',
      errorStatus: error ? error.status : 'unknown',
      data,
    });
    return;
  }
  if (client.realtime.accessTokenValue !== data.session.access_token) {
    yield client.realtime.setAuth();
    ddt.log('damz.auth', 'Refreshed session');
  }
}
